/**
 * MbuziPlusParent.jsx
 *
 * The main MbuziPlus entry point. This is not the training/registration flow —
 * that lives in MbuziPlusTraining.jsx and is reached from here. This component's
 * job is to explain what MbuziPlus actually is: a managed dairy goat value chain,
 * not just a training course — and to keep pointing people at registration the
 * whole way down the page.
 *
 * Design language: matches ImfuyoAcademy.jsx — Inter/DM Sans, one accent green,
 * flat quiet cards, no emoji, no gradients on surfaces, restraint over decoration.
 * Scroll reveals and image parallax borrow the motion language used on the
 * Bernard Njathi Wanjohi landing page (framer-motion whileInView + scroll-linked
 * transforms), kept quieter here to match this page's calmer register.
 *
 * v3 notes (this pass):
 * - New "Training Tracks" section spells out what T1, T2 and T3 actually cover —
 *   previously this only lived as a compact tag row in the Academy CTA. Each
 *   track now has its own quiet card with a short summary, a bullet list of
 *   what happens in it, and its own register/learn-more action. T3 is visually
 *   marked as a separate line item (dashed border, "billed separately" note) so
 *   nobody mistakes it for being covered by the KES 4,900 academy fee.
 * - New in-page Gallery section, reading from the same Firestore document
 *   MbuziPlusTraining.jsx's gallery uses (mbuziplusprogram/gallery) — so photos
 *   uploaded once show up in both places. Rebuilt from scratch in this page's
 *   own quiet-card language (no dark moody skin, no per-tag rainbow) rather than
 *   reusing MbuziPlusGallery.jsx's styling wholesale.
 * - Registration is now a running thread rather than a single CTA at the end:
 *   a slim top announcement bar, a reusable <RegisterCTA> strip dropped after
 *   most sections with section-specific copy, and a floating "Register" pill
 *   that appears once the hero has scrolled past and stays with the reader.
 * - Everything above reuses the existing tokens (quietCardStyle, IconChip,
 *   Reveal, useHoverLift, mbp-accent-hover) instead of inventing new ones, per
 *   the direction to keep this consistent with the rest of the page.
 *
 * NOTE ON IMAGES: no image files were attached when this was built, so photo
 * placements use placeholder Unsplash/Cloudinary URLs. Swap HERO_IMAGE, HERO_IMAGE_ALT,
 * and JOURNEY_IMAGE for your own Cloudinary assets (same pattern used
 * elsewhere in the codebase) — all have onError fallbacks so a dead link
 * won't break layout.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  ChevronRight, ChevronLeft, ArrowRight, ArrowUpRight, BookOpen, Microscope, CreditCard, Lock, Heart,
  Tractor, Handshake, Users, MapPin, BarChart3, Check, X, Droplet, Sprout,
  GitBranch, TrendingUp, Target, ArrowDown, Play, ShieldCheck, Images,
  Megaphone, Calendar, ExternalLink,
} from "lucide-react";
import { db } from "../Libs/firebase-config.mjs";
import { doc, getDoc } from "firebase/firestore";

import { PaymentConfirmModal } from "./MbuziPlusTraining";
import NxPage from "./nx/NxPage";
import JourneyFlow from "./JourneyFlow";
import { mbuziHtml } from "./nx/pages";

/* ─── PLACEHOLDER IMAGES — replace with your own assets ─────────────── */
const HERO_IMAGE = "https://res.cloudinary.com/dpymwa41m/image/upload/v1777087249/mbuziplus-hero/qjiavva0ak5qfngckuxl.jpg";
const HERO_IMAGE_ALT = "https://res.cloudinary.com/dpymwa41m/image/upload/v1777087287/mbuziplus-hero/ynbhshg30ghdyq96kdl6.jpg";
const JOURNEY_IMAGE = "https://res.cloudinary.com/dpymwa41m/image/upload/v1777087235/mbuziplus-hero/ef2rkckzequ01skwmkd4.jpg";
const IMFUYO_LOGO = "https://res.cloudinary.com/dpymwa41m/image/upload/v1777644987/blogs/covers/bjawnbvrnuxrtoxe9zmq.png";

/* ─── PRICING (kept in sync with MbuziPlusTraining.jsx) ──────────────── */
const ORIGINAL_PRICE = 7000;
const CURRENT_PRICE  = 4900;
const INSTALLMENT_DEPOSIT = 2450;

/* ─── SIGNATURE MARK — simple line-art goat icon, no uploaded asset used ── */
function GoatIcon({ size = 14, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 10c-1.2-1.4-1.6-3.2-1-5 1.3.2 2.4.9 3.2 2" />
      <path d="M18 10c1.2-1.4 1.6-3.2 1-5-1.3.2-2.4.9-3.2 2" />
      <path d="M7 9c0-2 1.8-4 5-4s5 2 5 4v3.2c0 1-.4 2-1.1 2.7l-1 1c-.4.4-.6.9-.6 1.4v2.2c0 1-.8 1.7-1.8 1.7h-1c-1 0-1.8-.7-1.8-1.7v-1.6" />
      <path d="M8.5 15.3v3c0 1-.8 1.9-1.8 1.9-.9 0-1.6-.6-1.8-1.5l-.6-3" />
      <circle cx="10" cy="9.5" r=".6" fill={color} stroke="none" />
      <circle cx="14" cy="9.5" r=".6" fill={color} stroke="none" />
    </svg>
  );
}

/* ─── IMFUYO LOGO — inline brand mark, falls back to GoatIcon if it fails ── */
function ImfuyoMark({ size = 14, color = "#3E7A32" }) {
  const [ok, setOk] = useState(true);
  if (!ok) return <GoatIcon size={size} color={color} />;
  return (
    <img
      src={IMFUYO_LOGO} alt="Imfuyo" loading="lazy"
      style={{ width: size, height: size, objectFit: "contain", display: "inline-block", flexShrink: 0 }}
      onError={() => setOk(false)}
    />
  );
}

/* Logo badge for larger, framed placements (e.g. the Imfuyo orchestrator
   node) — sits on its own light chip so the mark stays legible on both
   light and dark backgrounds. */
function ImfuyoBadge({ size = 26 }) {
  const [ok, setOk] = useState(true);
  return ok ? (
    <img
      src={IMFUYO_LOGO} alt="Imfuyo" loading="lazy"
      style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
      onError={() => setOk(false)}
    />
  ) : (
    <GoatIcon size={size} color="#285020" />
  );
}

/* ─── SCROLL REVEAL — framer-motion whileInView, reduced-motion aware ──
   Swapped from a hand-rolled IntersectionObserver to framer-motion so the
   easing/stagger language matches the rest of the Imfuyo site family. */
function Reveal({ children, delay = 0, y = 16 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: delay / 1000, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

/* ─── PARALLAX PHOTO — gentle scroll-linked drift, no static box ─────── */
function ParallaxPhoto({ src, alt, isDark, border, aspect = "4/5", radius = "20px", strength = 26, imgOk, setImgOk, fallback }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-strength, strength]);

  return (
    <div
      ref={ref}
      style={{
        borderRadius: radius, overflow: "hidden", aspectRatio: aspect,
        background: isDark ? "#1f2937" : "#f0f0ed", border: `1px solid ${border}`,
        position: "relative",
      }}
    >
      {imgOk ? (
        <motion.img
          src={src} alt={alt} loading="lazy"
          style={{ width: "100%", height: "130%", objectFit: "cover", y, position: "relative", top: "-8%" }}
          onError={() => setImgOk(false)}
        />
      ) : (
        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {fallback}
        </div>
      )}
    </div>
  );
}

/* ─── DATA ────────────────────────────────────────────────────────────── */
const STATS = [
  { icon: Users, current: "1,000+", currentLabel: "farmers today", target: "10,000+", targetLabel: "farmers by 2028", label: "Farmers in the pipeline" },
  { icon: MapPin, current: "12", currentLabel: "counties today", target: "47", targetLabel: "counties, full coverage", label: "County reach" },
  { icon: Handshake, current: "10+", currentLabel: "partners today", target: "New each quarter", targetLabel: "", label: "Ecosystem partners" },
  { icon: BarChart3, current: "7", currentLabel: "layers, one system", target: "One record", targetLabel: "per farmer, for life", label: "Managed as one system" },
];

const PILLARS = [
  { icon: BookOpen, title: "Education", short: "Training before the animal or the debt.",
    body: "Structured, data-informed learning on dairy goat economics, breed selection, feeding, and record-keeping — calibrated to the same numbers a lender will later use to assess the farmer." },
  { icon: Microscope, title: "Genetics", short: "Verified breeds, known productivity.",
    body: "Partnered breeders supply dairy goats with documented genetic and productivity profiles — the first data point in the asset file, and the input every layer after this depends on." },
  { icon: CreditCard, title: "Financing", short: "The goat as collateral.",
    body: "Structured credit through partners like SBM Bank, using the goat itself as security under Kenya's Movable Property Security Rights Act. Farm data makes the farmer legible to a lender." },
  { icon: Lock, title: "Insurance", short: "One policy, goat and farmer.",
    body: "Bancassurance covering the animal and the farmer together. Claims are substantiated by tracked records instead of self-reporting, which lowers cost and speeds up payout." },
  { icon: Heart, title: "Veterinary care", short: "Health, logged.",
    body: "Ongoing veterinary access keeps the animal productive and creates the health record that insurers and lenders rely on — problems get caught before they become claims or bad debt." },
  { icon: Tractor, title: "Feed access", short: "Consistent input, predictable output.",
    body: "Reliable, quality feed addresses the single biggest driver of variable milk yield, and standardises the cost side of the farmer's unit economics." },
  { icon: Handshake, title: "Market access", short: "Milk, manure, and breeding income.",
    body: "A standing milk offtaker removes market risk. Manure is aggregated and sold on the farmer's behalf, and breeding stock creates a further income stream." },
];

const FLOW_STEPS = [
  { icon: BookOpen, label: "Education data", note: "Farmer readiness" },
  { icon: Microscope, label: "Genetics data", note: "Asset provenance" },
  { icon: BarChart3, label: "Farm data", note: "Health & production" },
  { icon: Lock, label: "Underwriting", note: "Insurance & credit" },
  { icon: TrendingUp, label: "Market data", note: "Revenue, repayment" },
];

const JOURNEY = [
  { n: "01", title: "Register", body: "Register for training with your name, ID, and phone number — no lengthy paperwork to start." },
  { n: "02", title: "Train — T1 & T2", body: "Learn the economics, health, and business model before committing to an animal." },
  { n: "03", title: "Get matched", body: "Receive a verified dairy goat from a partnered breeder with a known productivity profile." },
  { n: "04", title: "Activate finance & cover", body: "Credit and bancassurance are set up against your farm record, not a guess." },
  { n: "05", title: "Farm with support", body: "Vets, feed access, and digital tracking continue for the working life of the enterprise." },
  { n: "06", title: "Sell with certainty", body: "Milk goes to a standing offtaker. Manure and breeding add further income on top." },
];

const REVENUE = [
  { icon: Droplet, title: "Milk", body: "The primary income — sold to a guaranteed offtaker, not into an uncertain local market." },
  { icon: Sprout, title: "Manure", body: "Aggregated and sold to horticultural and organic farms on the farmer's behalf." },
  { icon: GitBranch, title: "Breeding", body: "Verified genetics create a market for kids and breeding stock beyond milk alone." },
];

/* Detailed breakdown of what each training track actually covers. T1 & T2
   are bundled into the KES 4,900 academy fee; T3 is deliberately styled as
   a separate line item (dashed border + its own "billed separately" note)
   so it never reads as included. */
const TRAINING_TIERS = [
  {
    tag: "T1",
    icon: BookOpen,
    title: "Dairy Goat Introduction",
    timing: "Week 1 · In person",
    feeNote: `Included in the KES ${CURRENT_PRICE.toLocaleString()} fee`,
    summary: "The business case, in plain numbers, before you commit to an animal or a shilling of debt.",
    bullets: [
      "Dairy goats as a business, not a hobby — real cost and revenue lines",
      "Breeds suited to Kenyan conditions, and how to tell them apart",
      "Daily care, feeding, and housing basics",
      "Milk yield economics — what a goat can realistically produce and earn",
      "The full Imfuyo value chain, so you know what happens after T1",
      "Starting from zero — worked examples with real numbers",
    ],
    accent: "#2563eb",
  },
  {
    tag: "T2",
    icon: Microscope,
    title: "Deep Dive With Dairy Goat Experts",
    timing: "In person · Runs straight after T1",
    feeNote: `Included in the KES ${CURRENT_PRICE.toLocaleString()} fee`,
    summary: "Face-to-face time with veterinarians, top-producing farmers, and agri-business coaches — the people, not just the material.",
    bullets: [
      "Animal health and disease prevention, taught by a practising vet",
      "Feed optimisation and nutrition for consistent milk yield",
      "How to scale a herd without losing control of costs",
      "Profit models and protecting your margin",
      "Market access and building offtaker relationships",
      "Open Q&A with the same experts who mentor Imfuyo's top farmers",
    ],
    accent: "#7c3aed",
  },
  {
    tag: "T3",
    icon: MapPin,
    title: "Real Farm Visits",
    timing: "On farm · Opens per trip, after T1 & T2",
    feeNote: "Billed separately per trip — not part of the KES 4,900 fee",
    summary: "Standing on a working farm Imfuyo helped build from scratch, watching the model run in real time.",
    bullets: [
      "Visit thriving farms Imfuyo has supported since their first goat",
      "Watch housing, feeding, and milking done properly, not just described",
      "Meet farmers who scaled and offtakers who buy from them",
      "Ask the questions that only make sense once you've seen the setup",
    ],
    accent: "#285020",
    dashed: true,
  },
];

/* Fallback partners — used until (or unless) mbuziplusprogram/main resolves.
   Shape mirrors what MbuziPlusTraining.jsx expects from Firestore: id, name,
   image (real logo url or null), initials (fallback), desc. */
const PARTNERS_FALLBACK = [
  { id: "kldc", name: "KLDC", image: null, initials: "KLDC", desc: "Kenya Livestock Development" },
  { id: "sbm", name: "SBM Bank", image: null, initials: "SBM", desc: "Financing & bancassurance" },
  { id: "giz", name: "GIZ", image: null, initials: "GIZ", desc: "German development cooperation" },
  { id: "jkuat", name: "JKUAT", image: null, initials: "JKU", desc: "Research & training" },
  { id: "muthigi", name: "Muthigi CBO", image: null, initials: "MC", desc: "Community mobilisation" },
  { id: "caisley", name: "Caisley", image: null, initials: "CA", desc: "Germany" },
  { id: "adt", name: "ADT", image: null, initials: "ADT", desc: "Germany" },
];

/* ─── GALLERY DATA HELPERS ────────────────────────────────────────────
   Reads the same Firestore doc MbuziPlusTraining.jsx's gallery reads
   (mbuziplusprogram/gallery), so photos uploaded once appear in both
   places. Kept deliberately simpler than MbuziPlusGallery.jsx — no
   masonry, no per-tag rainbow, no lightbox chrome that breaks from this
   page's quiet-card language. */
function buildGalleryCdnUrl(src, width) {
  if (!src) return src;
  if (src.includes("res.cloudinary.com")) {
    return src.replace("/upload/", `/upload/w_${width},q_auto,f_auto/`);
  }
  return src;
}

function flattenGalleryDoc(data) {
  if (!data) return [];
  if (Array.isArray(data.items)) {
    return data.items.filter((it) => it.src);
  }
  if (Array.isArray(data.albums)) {
    return data.albums
      .filter((a) => a.visible !== false)
      .flatMap((a) =>
        (a.items || [])
          .filter((it) => it.src)
          .map((it) => ({ ...it, tag: it.tag || a.tag || "", caption: it.caption || "" }))
      );
  }
  return [];
}

/* ─── SECTION HEADER — one repeating atom, matches ImfuyoAcademy hero ── */
function SectionHeader({ eyebrow, title, sub, isDark, align = "left" }) {
  const textMain = isDark ? "#f9fafb" : "#0d1f16";
  const textSub = isDark ? "#c3cad2" : "#4b5768";
  const items = align === "center" ? "center" : "flex-start";
  const textAlign = align === "center" ? "center" : "left";
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: items, textAlign, marginBottom: "40px" }}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
        <span style={{ width: "18px", height: "2px", background: "#3E7A32", flexShrink: 0 }} />
        <span style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: textSub }}>
          {eyebrow}
        </span>
      </div>
      <h2 style={{ fontSize: "clamp(28px,4vw,42px)", fontWeight: 800, letterSpacing: "-0.03em", color: textMain, margin: 0, lineHeight: 1.08, maxWidth: "620px" }}>
        {title}
      </h2>
      {sub && (
        <p style={{ fontSize: "15px", color: textSub, lineHeight: 1.7, margin: "14px 0 0", maxWidth: "540px" }}>
          {sub}
        </p>
      )}
    </div>
  );
}

/* ─── QUIET CARD — the one card style used everywhere ────────────────── */
function quietCardStyle(isDark, accented = false) {
  return {
    background: isDark ? "#1f2937" : "#fff",
    border: `1px solid ${accented ? "#22c55e55" : (isDark ? "#374151" : "rgba(0,0,0,0.08)")}`,
    borderRadius: "16px",
    transition: "transform 0.2s, box-shadow 0.2s",
    position: "relative",
    overflow: "hidden",
  };
}

function useHoverLift(isDark) {
  return {
    onMouseEnter: (e) => {
      e.currentTarget.style.transform = "translateY(-2px)";
      e.currentTarget.style.boxShadow = isDark ? "0 12px 32px rgba(0,0,0,0.35)" : "0 12px 32px rgba(0,0,0,0.08)";
    },
    onMouseLeave: (e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "none";
    },
  };
}

/* ─── ICON CHIP — matches StatCard's icon treatment ───────────────────── */
function IconChip({ Icon, isDark, size = 36, color = "#3E7A32", bg }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: "10px",
      background: bg || (isDark ? "#14301A" : "#F1F5EE"),
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}>
      <Icon size={size * 0.44} color={color} />
    </div>
  );
}

/* ─── PROJECTION CELL — bordered data-grid cell, today → target ──────── */
function ProjectionCell({ icon, current, currentLabel, target, targetLabel, label, isDark, isLastCol, isLastRow }) {
  const textMain = isDark ? "#f9fafb" : "#0d1f16";
  const textSub = isDark ? "#c3cad2" : "#4b5768";
  return (
    <div
      className="mbp-accent-hover"
      style={{
        padding: "20px 20px 18px",
        borderRight: isLastCol ? "none" : `1px solid ${isDark ? "#374151" : "#e5e3dc"}`,
        borderBottom: isLastRow ? "none" : `1px solid ${isDark ? "#374151" : "#e5e3dc"}`,
        position: "relative", overflow: "hidden",
      }}
    >
      <IconChip Icon={icon} isDark={isDark} size={32} />
      <p style={{ margin: "14px 0 2px", fontSize: "22px", fontWeight: 800, color: textMain, letterSpacing: "-0.02em" }}>{current}</p>
      <p style={{ margin: 0, fontSize: "11.5px", color: textSub, fontWeight: 500 }}>{currentLabel}</p>

      <div style={{ display: "flex", alignItems: "center", gap: "6px", margin: "12px 0 3px" }}>
        <ArrowRight size={11} color="#3E7A32" style={{ flexShrink: 0 }} />
        <span style={{ fontSize: "13px", fontWeight: 700, color: "#285020" }}>{target}</span>
      </div>
      {targetLabel && <p style={{ margin: 0, fontSize: "11.5px", color: isDark ? "#B7D9A8" : "#14301A", fontWeight: 500 }}>{targetLabel}</p>}

      <p style={{ margin: "12px 0 0", fontSize: "10.5px", color: textSub, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", opacity: 0.85 }}>{label}</p>
    </div>
  );
}

/* ─── PILLAR CARD — expandable, single accent, no per-pillar colors ──── */
function PillarCard({ pillar, isDark, open, onToggle }) {
  const hover = useHoverLift(isDark);
  const textMain = isDark ? "#f9fafb" : "#0d1f16";
  const textSub = isDark ? "#c3cad2" : "#4b5768";
  return (
    <button
      onClick={onToggle}
      className="mbp-accent-hover"
      style={{
        ...quietCardStyle(isDark, open),
        padding: "20px", textAlign: "left", cursor: "pointer",
        fontFamily: "inherit", width: "100%", display: "block",
      }}
      {...hover}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: "14px" }}>
        <IconChip Icon={pillar.icon} isDark={isDark} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
            <h3 style={{ margin: 0, fontSize: "15.5px", fontWeight: 700, color: textMain, letterSpacing: "-0.01em" }}>{pillar.title}</h3>
            <ChevronRight size={15} color={textSub} style={{ flexShrink: 0, transform: open ? "rotate(90deg)" : "none", transition: "transform 0.2s" }} />
          </div>
          <p style={{ margin: "3px 0 0", fontSize: "12.5px", color: textSub, fontWeight: 500 }}>{pillar.short}</p>
          <div style={{
            maxHeight: open ? "140px" : "0px",
            opacity: open ? 1 : 0,
            overflow: "hidden",
            transition: "max-height 0.3s ease, opacity 0.25s ease, margin 0.3s ease",
            marginTop: open ? "10px" : "0px",
          }}>
            <p style={{ margin: 0, fontSize: "13.5px", color: textSub, lineHeight: 1.65 }}>{pillar.body}</p>
          </div>
        </div>
      </div>
    </button>
  );
}

/* ─── TIER CARD — full T1/T2/T3 breakdown, T3 marked as a separate fee ── */
function TierCard({ tier, isDark, onRegister }) {
  const hover = useHoverLift(isDark);
  const textMain = isDark ? "#f9fafb" : "#0d1f16";
  const textSub = isDark ? "#c3cad2" : "#4b5768";
  const border = isDark ? "#374151" : "#e5e3dc";
  return (
    <div
      className="mbp-accent-hover"
      style={{
        ...quietCardStyle(isDark),
        border: `${tier.dashed ? "1.5px dashed" : "1px solid"} ${tier.dashed ? border : (isDark ? "#374151" : "rgba(0,0,0,0.08)")}`,
        padding: "26px", display: "flex", flexDirection: "column", gap: "16px",
      }}
      {...hover}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "14px", flex: 1, minWidth: "200px" }}>
          <IconChip Icon={tier.icon} isDark={isDark} size={42} color={tier.accent} bg={isDark ? "#111827" : `${tier.accent}12`} />
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              <span style={{ fontSize: "11px", fontWeight: 800, color: tier.accent, background: isDark ? "#111827" : `${tier.accent}12`, borderRadius: "50px", padding: "2px 9px" }}>
                {tier.tag}
              </span>
              <h3 style={{ margin: 0, fontSize: "17px", fontWeight: 800, color: textMain, letterSpacing: "-0.01em" }}>{tier.title}</h3>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "5px", marginTop: "5px" }}>
              <Calendar size={11} color={textSub} style={{ flexShrink: 0 }} />
              <span style={{ fontSize: "12px", color: textSub, fontWeight: 500 }}>{tier.timing}</span>
            </div>
          </div>
        </div>
        <span style={{
          fontSize: "11px", fontWeight: 700, whiteSpace: "nowrap",
          color: tier.dashed ? "#b45309" : "#285020",
          background: tier.dashed ? (isDark ? "#1e1400" : "#fffbeb") : (isDark ? "#14301A" : "#F1F5EE"),
          border: `1px solid ${tier.dashed ? (isDark ? "#78350f" : "#fde68a") : (isDark ? "#14301A" : "#D5E8CB")}`,
          borderRadius: "50px", padding: "4px 10px",
        }}>
          {tier.feeNote}
        </span>
      </div>

      <p style={{ margin: 0, fontSize: "14px", color: textSub, lineHeight: 1.7 }}>{tier.summary}</p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: "8px" }}>
        {tier.bullets.map((b) => (
          <div key={b} style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
            <Check size={13} color={tier.accent} style={{ flexShrink: 0, marginTop: "3px" }} />
            <span style={{ fontSize: "13px", color: textSub, lineHeight: 1.55 }}>{b}</span>
          </div>
        ))}
      </div>

      <button
        onClick={onRegister}
        style={{
          alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: "6px",
          background: "transparent", color: tier.accent, border: `1.5px solid ${tier.accent}`,
          borderRadius: "10px", padding: "9px 16px", fontSize: "13px", fontWeight: 700,
          cursor: "pointer", fontFamily: "inherit", marginTop: "2px",
        }}
      >
        {tier.dashed ? "See T3 sign-up" : "Register for this track"} <ChevronRight size={14} />
      </button>
    </div>
  );
}

/* ─── FLOATING REGISTER PILL — persistent reminder once past the hero ── */
/* Experts: read from the same backend list that fed the training page (mbuziplusprogram / main), so names, photos and topics are the ones already listed. */
function ExpertsSection() {
  const [experts, setExperts] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "mbuziplusprogram", "main"));
        const list = snap.exists() ? snap.data().experts : null;
        if (alive && Array.isArray(list) && list.length) setExperts(list.filter((e) => e && e.name));
      } catch (e) { /* keep the listed experts */ }
    })();
    return () => { alive = false; };
  }, []);
  if (!experts.length) return null;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(230px,1fr))", gap: "20px" }}>
      {experts.map((e) => (
        <div key={e.id || e.name} style={{ background: "#fff", border: "1px solid #E9E3D3", borderRadius: "20px", overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <div style={{ height: "210px", background: "#E9E3D3" }}>
            {e.image ? <img src={e.image} alt={e.name} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top", display: "block" }} onError={(ev) => { ev.currentTarget.style.display = "none"; }} /> : null}
          </div>
          <div style={{ padding: "16px 18px 20px", display: "flex", flexDirection: "column", gap: "4px" }}>
            <div style={{ fontWeight: 800, fontSize: "18px", color: "#14301A" }}>{e.name}</div>
            <div style={{ fontWeight: 700, fontSize: "13px", letterSpacing: "0.04em", textTransform: "uppercase", color: "#C2410C" }}>{e.role}</div>
            {e.org ? <div style={{ fontSize: "14px", color: "#3B4A38" }}>{e.org}</div> : null}
            {e.years ? <div style={{ fontSize: "14px", color: "#3B4A38" }}>{String(e.years).replace(/yrs?/i, "years")} of experience</div> : null}
            {e.topic ? <div style={{ marginTop: "8px", fontSize: "15px", lineHeight: 1.5, color: "#14301A", background: "#F4F0E6", borderRadius: "12px", padding: "10px 12px" }}>{e.topic}</div> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

function FloatingRegisterPill({ isDark, onRegister }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 560);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div
      style={{
        position: "fixed", bottom: "20px", right: "20px", zIndex: 60,
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(12px)",
        pointerEvents: visible ? "auto" : "none",
        transition: "opacity 0.25s ease, transform 0.25s ease",
      }}
    >
      <button
        onClick={onRegister}
        style={{
          display: "inline-flex", alignItems: "center", gap: "8px",
          background: "#285020", color: "#fff", border: "1.5px solid rgba(255,255,255,0.25)",
          borderRadius: "50px", padding: "12px 18px 12px 14px",
          fontSize: "13.5px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
          boxShadow: isDark ? "0 10px 32px rgba(0,0,0,0.5)" : "0 10px 32px rgba(22,163,74,0.38)",
        }}
      >
        <GoatIcon size={15} color="#fff" /> Register and pay
      </button>
    </div>
  );
}

/* ─── PARTNER CARD — logo is the focal point, name/desc sit underneath ── */
function PartnerCard({ p, isDark }) {
  const [imgOk, setImgOk] = useState(true);
  const hover = useHoverLift(isDark);
  const textMain = isDark ? "#f9fafb" : "#0d1f16";
  const textSub = isDark ? "#c3cad2" : "#4b5768";
  const showImage = Boolean(p.image) && imgOk;
  return (
    <div
      className="mbp-accent-hover"
      style={{ ...quietCardStyle(isDark), padding: "18px", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: "14px" }}
      {...hover}
    >
      <div style={{
        width: "100%", height: "96px", borderRadius: "12px", flexShrink: 0,
        background: isDark ? "#14301A" : "#F1F5EE",
        display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", padding: "12px",
      }}>
        {showImage ? (
          <img
            src={p.image} alt={p.name} loading="lazy"
            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
            onError={() => setImgOk(false)}
          />
        ) : (
          <span style={{ fontSize: "22px", fontWeight: 800, color: "#285020", letterSpacing: "-0.02em" }}>
            {(p.initials || p.name.split(" ").map(w => w[0]).join("")).slice(0, 3).toUpperCase()}
          </span>
        )}
      </div>
      <div style={{ minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: "13.5px", fontWeight: 700, color: textMain }}>{p.name}</p>
        <p style={{ margin: "2px 0 0", fontSize: "11.5px", color: textSub }}>{p.desc}</p>
      </div>
    </div>
  );
}

/* ─── GALLERY CARD — quiet-card photo tile, fixed aspect, no cropping surprises ── */
function GalleryCard({ item, isDark, onOpen }) {
  const [imgOk, setImgOk] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const hover = useHoverLift(isDark);
  const isVideo = item.type === "video";
  const textMain = isDark ? "#f9fafb" : "#0d1f16";

  return (
    <div
      className="mbp-accent-hover"
      style={{ ...quietCardStyle(isDark), overflow: "hidden", cursor: "pointer" }}
      onClick={() => (isVideo ? window.open(item.src, "_blank", "noopener,noreferrer") : onOpen(item))}
      {...hover}
    >
      <div style={{ position: "relative", aspectRatio: "4/3", background: isDark ? "#111827" : "#f4f4f2" }}>
        {!loaded && (
          <div style={{
            position: "absolute", inset: 0,
            background: isDark ? "#1f2937" : "#eceae4",
          }} />
        )}
        {imgOk ? (
          <img
            src={buildGalleryCdnUrl(item.src, 640)}
            alt={item.caption || "MbuziPlus"}
            loading="lazy"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: loaded ? 1 : 0, transition: "opacity 0.3s ease" }}
            onLoad={() => setLoaded(true)}
            onError={() => setImgOk(false)}
          />
        ) : (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <GoatIcon size={26} color={isDark ? "#4b5563" : "#d1d5db"} />
          </div>
        )}
        {isVideo && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ width: 38, height: 38, borderRadius: "50%", background: "rgba(22,163,74,0.92)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 6px 18px rgba(0,0,0,0.3)" }}>
              <Play size={15} color="#fff" fill="#fff" style={{ marginLeft: 2 }} />
            </div>
          </div>
        )}
        {item.tag && (
          <span style={{
            position: "absolute", top: "8px", left: "8px",
            background: "rgba(17,24,39,0.72)", color: "#fff",
            fontSize: "10px", fontWeight: 700, letterSpacing: "0.03em",
            padding: "3px 8px", borderRadius: "50px",
          }}>
            {item.tag}
          </span>
        )}
      </div>
      {item.caption && (
        <div style={{ padding: "10px 12px" }}>
          <p style={{ margin: 0, fontSize: "12.5px", color: textMain, fontWeight: 600, lineHeight: 1.5 }}>{item.caption}</p>
        </div>
      )}
    </div>
  );
}

/* ─── GALLERY LIGHTBOX — plain, quiet, keyboard + arrow navigable ────── */
function GalleryLightbox({ items, index, onClose, onNav }) {
  const item = items[index];
  const hasPrev = index > 0;
  const hasNext = index < items.length - 1;

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && hasPrev) onNav(index - 1);
      if (e.key === "ArrowRight" && hasNext) onNav(index + 1);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose, onNav, index, hasPrev, hasNext]);

  if (!item) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 200,
        background: "rgba(13,17,23,0.92)", backdropFilter: "blur(10px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: "24px",
      }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        style={{
          position: "absolute", top: "18px", right: "18px",
          width: "38px", height: "38px", borderRadius: "50%",
          background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)",
          color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <X size={16} />
      </button>

      {hasPrev && (
        <button
          onClick={(e) => { e.stopPropagation(); onNav(index - 1); }}
          style={{
            position: "absolute", left: "18px", top: "50%", transform: "translateY(-50%)",
            width: "42px", height: "42px", borderRadius: "50%",
            background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)",
            color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <ChevronLeft size={18} />
        </button>
      )}
      {hasNext && (
        <button
          onClick={(e) => { e.stopPropagation(); onNav(index + 1); }}
          style={{
            position: "absolute", right: "18px", top: "50%", transform: "translateY(-50%)",
            width: "42px", height: "42px", borderRadius: "50%",
            background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)",
            color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <ChevronRight size={18} />
        </button>
      )}

      <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", maxWidth: "92vw" }}>
        <img
          src={buildGalleryCdnUrl(item.src, 1400)}
          alt={item.caption || "MbuziPlus"}
          style={{ maxWidth: "min(88vw,1100px)", maxHeight: "78vh", width: "auto", height: "auto", objectFit: "contain", borderRadius: "10px", boxShadow: "0 24px 64px rgba(0,0,0,0.6)" }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {item.caption && <p style={{ margin: 0, fontSize: "13px", color: "rgba(255,255,255,0.85)", fontWeight: 600 }}>{item.caption}</p>}
          <span style={{ fontSize: "11px", color: "rgba(255,255,255,0.4)", letterSpacing: "0.04em" }}>{index + 1} / {items.length}</span>
        </div>
      </div>
    </motion.div>
  );
}

/* ─── GALLERY SECTION ──────────────────────────────────────────────────
   Fetches mbuziplusprogram/gallery — the same document MbuziPlusTraining
   reads via MbuziPlusGallery.jsx — so a photo added once shows up in both
   places without any duplicate data entry. */
function GallerySection({ isDark }) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ready | empty | error
  const [visibleCount, setVisibleCount] = useState(8);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "mbuziplusprogram", "gallery"));
        if (cancelled) return;
        if (!snap.exists()) { setStatus("empty"); return; }
        const flat = flattenGalleryDoc(snap.data());
        if (flat.length === 0) { setStatus("empty"); return; }
        setItems(flat);
        setStatus("ready");
      } catch (err) {
        console.warn("Could not load MbuziPlus gallery:", err);
        if (!cancelled) setStatus("error");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const photoItems = useMemo(() => items.filter((it) => it.type !== "video"), [items]);
  // Show the album only when it has photos: no error or "no photos yet" cards on the public page.
  const hideAlbum = status === "error" || status === "empty";
  const visibleItems = items.slice(0, visibleCount);
  const hasMore = visibleCount < items.length;

  const openLightbox = useCallback((item) => {
    const idx = photoItems.findIndex((p) => p.src === item.src);
    setLightboxIndex(idx >= 0 ? idx : 0);
  }, [photoItems]);

  const textSub = isDark ? "#c3cad2" : "#4b5768";
  const textMain = isDark ? "#f9fafb" : "#0d1f16";
  const border = isDark ? "#374151" : "#e5e3dc";
  const cardBg = isDark ? "#1f2937" : "#fff";

  if (hideAlbum) return null;

  return (
    <section id="gallery" style={{ maxWidth: "1100px", margin: "0 auto", padding: "0 32px 72px" }}>
      <Reveal>
        <SectionHeader
          isDark={isDark}
          eyebrow="See It In Action"
          title="Training sessions, farm visits, and cohorts"
          sub="Photos and video from T1 and T2 sessions, T3 farm visits, and community activations — pulled from the same album the training portal uses."
        />
      </Reveal>

      {status === "loading" && (
        <div className="mbp-grid-gallery">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} style={{ aspectRatio: "4/3", borderRadius: "16px", background: isDark ? "#1f2937" : "#eceae4", border: `1px solid ${border}` }} />
          ))}
        </div>
      )}

      {status === "error" && (
        <div style={{ ...quietCardStyle(isDark), padding: "28px", textAlign: "center" }}>
          <p style={{ margin: 0, fontSize: "13.5px", color: textSub }}>Couldn't load the gallery right now — the rest of the page still works fine.</p>
        </div>
      )}

      {status === "empty" && (
        <div style={{ ...quietCardStyle(isDark), padding: "28px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
          <IconChip Icon={Images} isDark={isDark} />
          <p style={{ margin: 0, fontSize: "13.5px", color: textSub }}>No photos yet — check back after the next cohort.</p>
        </div>
      )}

      {status === "ready" && (
        <>
          <Reveal delay={80}>
            <div className="mbp-grid-gallery">
              {visibleItems.map((item, i) => (
                <GalleryCard key={item.id || `${item.src}-${i}`} item={item} isDark={isDark} onOpen={openLightbox} />
              ))}
            </div>
          </Reveal>

          <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
            {hasMore && (
              <button
                onClick={() => setVisibleCount((c) => c + 8)}
                style={{
                  display: "inline-flex", alignItems: "center", gap: "7px",
                  background: cardBg, color: textMain, border: `1.5px solid ${border}`,
                  borderRadius: "10px", padding: "11px 20px", fontSize: "13px", fontWeight: 700,
                  cursor: "pointer", fontFamily: "inherit",
                }}
              >
                Show more photos <ChevronRight size={14} />
              </button>
            )}
          </div>

          <AnimatePresence>
            {lightboxIndex !== null && (
              <GalleryLightbox
                items={photoItems}
                index={lightboxIndex}
                onClose={() => setLightboxIndex(null)}
                onNav={setLightboxIndex}
              />
            )}
          </AnimatePresence>
        </>
      )}
    </section>
  );
}

/* ─── MAIN COMPONENT ───────────────────────────────────────────────────── */
export default function MbuziPlusParent({ isDark = false, onBack, onNavigate }) {
  const [view, setView] = useState(() => {
    // The Services page can open the registration and payments lookup directly.
    try { const v = sessionStorage.getItem("imfuyo-mbp-view"); if (v) { sessionStorage.removeItem("imfuyo-mbp-view"); if (v === "training") return "training"; } } catch (e) { /* ignore */ }
    return "overview";
  }); // "overview" | "training"
  const [openPillar, setOpenPillar] = useState(0);
  const [heroImgOk, setHeroImgOk] = useState(true);
  const [heroAltImgOk, setHeroAltImgOk] = useState(true);
  const [journeyImgOk, setJourneyImgOk] = useState(true);
  const [partners, setPartners] = useState(PARTNERS_FALLBACK);

  // Fetch real partner logos from the same Firestore doc MbuziPlusTraining
  // reads (mbuziplusprogram/main). Falls back silently to the local list.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "mbuziplusprogram", "main"));
        if (!cancelled && snap.exists()) {
          const data = snap.data();
          if (data.partners?.length > 0) setPartners(data.partners);
        }
      } catch (err) {
        console.warn("Could not fetch MbuziPlus partners, using defaults:", err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Reset scroll to the top whenever the overview/training view swaps, so
  // registering never leaves someone stranded mid-page.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [view]);

  // One place to register and pay: the Marketplace (Paystack). The older training page stays only for people who already registered by bank payment.
  const goToTraining = () => { window.location.assign("/shop/products/mbuziplus-course"); };
  const goToExisting = () => setView("training");
  const goToOverview = () => setView("overview");

  if (view === "training") {
    // For people who registered earlier by bank payment. New registrations are made in the Marketplace.
    return (
      <div style={{ minHeight: "100vh", background: isDark ? "#111827" : "#fafaf8", fontFamily: "'Inter','DM Sans',system-ui,sans-serif", padding: "96px 24px 48px" }}>
        <div style={{ maxWidth: "560px", margin: "0 auto" }}>
          <button onClick={goToOverview} style={{ background: "none", border: "none", cursor: "pointer", color: isDark ? "#c3cad2" : "#4b5768", fontSize: "14px", fontWeight: 600, padding: 0, marginBottom: "20px", fontFamily: "inherit" }}>← Back to MbuziPlus</button>
          <h1 style={{ margin: "0 0 10px", fontSize: "28px", fontWeight: 800, color: isDark ? "#f9fafb" : "#0d1f16" }}>Check your registration and payments</h1>
          <p style={{ margin: 0, fontSize: "15px", lineHeight: 1.7, color: isDark ? "#c3cad2" : "#4b5768" }}>
            This is for people who registered earlier and paid by bank. Enter the phone number you registered with. To register now, use the Marketplace.
          </p>
          <button onClick={goToTraining} style={{ marginTop: "20px", background: "#285020", color: "#fff", border: "none", borderRadius: "12px", padding: "14px 24px", fontSize: "14.5px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Register and pay in the Marketplace</button>
        </div>
        <PaymentConfirmModal onClose={goToOverview} isDark={isDark} />
      </div>
    );
  }

  const bg = isDark ? "#111827" : "#fafaf8";
  const cardBg = isDark ? "#1f2937" : "#fff";
  const textMain = isDark ? "#f9fafb" : "#0d1f16";
  const textSub = isDark ? "#c3cad2" : "#4b5768";
  const border = isDark ? "#374151" : "#e5e3dc";

  const h2Style  = { fontSize: "clamp(28px,4vw,42px)", fontWeight: 800, letterSpacing: "-0.03em", color: textMain, margin: "0 0 0.75rem", lineHeight: 1.08 };
  const subStyle = { fontSize: "15px", color: textSub, maxWidth: "520px", margin: "0 auto", lineHeight: 1.75 };

  const globalCss = `
    html { scroll-behavior: smooth; }
    @keyframes mbpPing { 0% { opacity: 0.55; } 100% { transform: scale(1.7); opacity: 0; } }
    .mbp-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
    .mbp-grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
    .mbp-grid-pillars { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
    .mbp-grid-stats { display: grid; grid-template-columns: repeat(4, 1fr); border: 1px solid ${border}; border-radius: 16px; overflow: hidden; }
    .mbp-grid-partners { display: grid; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); gap: 14px; }
    .mbp-grid-tiers { display: grid; grid-template-columns: 1fr; gap: 14px; }
    .mbp-grid-gallery { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 14px; }
    .mbp-flow { display: flex; align-items: stretch; gap: 0; }
    .mbp-flow-arrow { display: flex; align-items: center; justify-content: center; flex: 0 0 auto; }
    .mbp-hero-grid { display: grid; grid-template-columns: 1.15fr 0.85fr; gap: 48px; align-items: center; }
    .mbp-journey-grid { display: grid; grid-template-columns: 1fr 0.7fr; gap: 40px; align-items: flex-start; }
    .mbp-top-banner { display: flex; align-items: center; justify-content: center; gap: 10px; flex-wrap: wrap; text-align: center; }
    .mbp-accent-hover::before {
      content: ""; position: absolute; top: 0; left: 0; right: 0; height: 3px;
      background: #3E7A32; transform: scaleX(0); transform-origin: left;
      transition: transform 0.35s ease; pointer-events: none;
    }
    .mbp-accent-hover:hover::before { transform: scaleX(1); }
    @media (max-width: 860px) {
      .mbp-hero-grid { grid-template-columns: 1fr; gap: 28px; }
      .mbp-journey-grid { grid-template-columns: 1fr; }
      .mbp-grid-3 { grid-template-columns: 1fr 1fr; }
      .mbp-grid-stats { grid-template-columns: repeat(2, 1fr); }
    }
    @media (max-width: 640px) {
      .mbp-grid-2 { grid-template-columns: 1fr; }
      .mbp-grid-pillars { grid-template-columns: 1fr; }
      .mbp-grid-3 { grid-template-columns: 1fr; }
      .mbp-grid-stats { grid-template-columns: 1fr; }
      .mbp-grid-gallery { grid-template-columns: repeat(2, 1fr); }
      .mbp-flow { flex-direction: column; }
      .mbp-flow-arrow { transform: rotate(90deg); padding: 4px 0; }
    }
  `;

  return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "'Inter','DM Sans',system-ui,sans-serif" }}>
      <style>{globalCss}</style>

      {/* ══ NEW MBUZIPLUS INTRO ══ */}
      <NxPage html={mbuziHtml} slots={{ experts: <ExpertsSection />, journey: <JourneyFlow onNavigate={onNavigate} onRegister={goToTraining} /> }} onNavigate={onNavigate} onRegister={goToTraining} scrollTop={false} />

      {/* ══ GALLERY — same Firestore source as the training portal ══ */}
      <GallerySection isDark={isDark} />

      {/* ══ EXISTING REGISTRANTS ══ */}
      <section style={{ maxWidth: "1100px", margin: "0 auto", padding: "0 32px 40px" }}>
        <p style={{ margin: 0, fontSize: "14px", color: textSub, lineHeight: 1.7 }}>
          Registered earlier by bank payment?{" "}
          <button onClick={goToExisting} style={{ background: "none", border: "none", padding: 0, color: "#285020", fontWeight: 700, textDecoration: "underline", cursor: "pointer", fontFamily: "inherit", fontSize: "inherit" }}>
            Check your registration and payments
          </button>
        </p>
      </section>

      {/* ══ DATA PROTECTION NOTE ══ */}
      <section style={{ maxWidth: "1100px", margin: "0 auto", padding: "0 32px 40px" }}>
        <Reveal>
          <div style={{
            display: "flex", alignItems: "flex-start", gap: "10px",
            padding: "14px 16px", background: isDark ? "#111827" : "#fafaf8",
            border: `1px solid ${border}`, borderRadius: "12px",
          }}>
            <ShieldCheck size={16} color="#285020" style={{ flexShrink: 0, marginTop: "1px" }} />
            <p style={{ margin: 0, fontSize: "12.5px", color: textSub, lineHeight: 1.65 }}>
              <strong style={{ color: textMain }}>ODPC registered</strong> — Data Protection Certificate No. 151-193C-B376. Any information you share when registering is handled carefully and used only to run the MbuziPlus programme.
            </p>
          </div>
        </Reveal>
      </section>

      {/* ══ FOOTER ══ */}
      <footer style={{ borderTop: `1px solid ${border}`, padding: "28px 32px" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <ImfuyoMark size={13} color="#3E7A32" />
            <span style={{ fontSize: "13px", fontWeight: 700, color: textMain }}>MbuziPlus</span>
            <span style={{ fontSize: "13px", color: textSub }}>by Imfuyo</span>
          </div>
          {onBack && (
            <button
              onClick={onBack}
              style={{ background: "none", border: "none", cursor: "pointer", color: textSub, fontSize: "13px", fontWeight: 600, fontFamily: "inherit" }}
            >
              ← Back
            </button>
          )}
        </div>
      </footer>

      {/* ══ FLOATING REGISTER PILL — persists through the whole scroll ══ */}
      <FloatingRegisterPill isDark={isDark} onRegister={goToTraining} />
    </div>
  );
}